/**
 * How big a file is, asked of the route that serves it.
 *
 * The number under a card used to ride on the message, where whoever wrote the
 * reply had put it. A model writes that field from memory: a film rendered
 * again into the same path was handed over under the previous cut's size, and
 * the card said 5.1 MB over a 5 MB file. A message cannot know better than
 * that — the file is rewritten in place after the message is on the record —
 * and the route can. It is what the player above the number plays, and it
 * answers `HEAD` with the length of what it would send now
 * (`workbench/server/app.py`: a file on this machine and a remote Agent's
 * alike).
 *
 * So a card asks when it is drawn and prints what comes back. No number is
 * kept from one drawing to the next: remembered per address, it would be the
 * same stale copy one step further on, and the next reply that hands over
 * that path would go out under the old size again. What is kept per address
 * is who is showing it now, so one answer reaches every card for that file —
 * they all play the same bytes.
 */

import { useEffect, useState } from "react";

/** How one card is told what the route answered. */
type Tell = (size: number | null) => void;

/** The cards on the page now, by the address each one asked about. */
const drawn = new Map<string, Set<Tell>>();

/**
 * Bytes, or `null` until the route has said and whenever it cannot.
 *
 * `null` is every way of not knowing — still asking, a file that is gone, an
 * Agent that is away, a proxy that dropped the header — and a card writes no
 * size for any of them (`kind.ts` `fileSizeWords`).
 */
export function useServedSize(url: string): number | null {
  const [size, setSize] = useState<number | null>(null);
  useEffect(() => {
    // A card that is now about another file starts from not knowing.
    setSize(null);
    const cards: Set<Tell> = drawn.get(url) ?? new Set();
    drawn.set(url, cards);
    cards.add(setSize);
    const asking = new AbortController();
    void ask(url, asking.signal).then((said) => {
      if (asking.signal.aborted) return;
      for (const tell of cards) tell(said);
    });
    return () => {
      asking.abort();
      cards.delete(setSize);
      if (!cards.size) drawn.delete(url);
    };
  }, [url]);
  return size;
}

/** One `HEAD`, and the length it answered with; `null` for anything else. */
async function ask(url: string, signal: AbortSignal): Promise<number | null> {
  try {
    const answer = await fetch(url, { method: "HEAD", signal });
    if (!answer.ok) return null;
    // Digits or nothing: `Number("")` is 0, and 0 would be a claim.
    const length = answer.headers.get("content-length") ?? "";
    return /^\d+$/.test(length) ? Number(length) : null;
  } catch {
    // Not reachable, or this card went away while it was asking.
    return null;
  }
}
